import { useSeoMeta } from '@unhead/react';
import { Link, useParams } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';

import { useForumNodes } from '@/hooks/useForum';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  childrenOf,
  nodeType,
  sortThreadsByActivity,
  threadStats,
  timeAgo,
  type ForumNode,
  type ForumTree,
} from '@/lib/forum';
import { ForumHeader } from './ForumHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { NewTopicDialog } from './NewTopicDialog';
import { EditNodeDialog } from './EditNodeDialog';
import { ForumFooter } from './ForumFooter';
import { AuthorName } from './Author';
import { Skeleton } from '@/components/ui/skeleton';

export function ForumView() {
  const { id } = useParams();
  const { data, isLoading } = useForumNodes();
  const { user } = useCurrentUser();

  useSeoMeta({
    title: 'NostrBoard — Forum',
    description: 'A Nostr-powered discussion forum.',
  });

  if (isLoading) return <ForumPageSkeleton />;
  if (!data) return <ForumPageSkeleton />;

  const forum = id ? data.byId.get(id) : undefined;
  if (!forum || nodeType(forum, data.byId) !== 'forum') {
    return <ForumMissing />;
  }

  const category = forum.parent ? data.byId.get(forum.parent) : undefined;
  const threads = sortThreadsByActivity(
    data.byId,
    childrenOf(data.byId, forum.id),
  );

  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-5">
        <Breadcrumbs
          items={[
            { label: 'Board index', to: '/' },
            ...(category ? [{ label: category.title || 'Category' }] : []),
            { label: forum.title || 'Forum' },
          ]}
        />

        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#105B86]">{forum.title || 'Untitled forum'}</h1>
            {forum.content && <p className="mt-1 text-sm text-[#666]">{forum.content}</p>}
          </div>
          <NewTopicDialog forumId={forum.id} />
        </div>

        <div className="overflow-hidden rounded-[6px] border border-[#d6d6d6] bg-white">
          <div className="border-b border-[#d6d6d6] bg-[linear-gradient(to_bottom,#f4f4f4,#e4e4e4)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#333]">
            Topics
          </div>
          {threads.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#777]">
              No topics yet. {user ? 'Click “New topic” to start one.' : 'Log in to post the first topic.'}
            </div>
          ) : (
            <div className="divide-y divide-[#ececec]">
              {threads.map((thread) => (
                <ThreadRow key={thread.id} thread={thread} tree={data} />
              ))}
            </div>
          )}
        </div>

        <ForumFooter />
      </main>
    </div>
  );
}

function ThreadRow({ thread, tree }: { thread: ForumNode; tree: ForumTree }) {
  const stats = threadStats(tree.byId, thread.id);
  const last = stats.lastPost;

  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#f6f9fc]">
      <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded bg-[#eaf2f9] text-[#105B86] sm:flex">
        <MessageSquare className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <Link
          to={`/t/${thread.id}`}
          className="text-[15px] font-bold text-[#105B86] hover:underline"
        >
          {thread.title || 'Untitled topic'}
        </Link>
        <p className="mt-0.5 text-xs text-[#888]">
          by <AuthorName pubkey={thread.pubkey} className="font-normal" />
        </p>
      </div>
      <div className="hidden w-20 shrink-0 text-center text-xs text-[#666] md:block">
        <span className="font-semibold text-[#333]">{stats.replies}</span> replies
      </div>
      <div className="hidden w-44 shrink-0 text-xs text-[#666] lg:block">
        {last ? (
          <div className="truncate">
            <span>Last post by </span>
            <AuthorName pubkey={last.pubkey} className="font-normal" />
            <div className="text-[#999]">{timeAgo(last.createdAt)}</div>
          </div>
        ) : (
          <span>No replies</span>
        )}
      </div>
      <EditNodeDialog node={thread} />
    </div>
  );
}

function ForumMissing() {
  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-10">
        <Breadcrumbs items={[{ label: 'Board index', to: '/' }, { label: 'Not found' }]} />
        <div className="rounded-[6px] border border-dashed border-[#c9c9c9] bg-white px-8 py-12 text-center">
          <p className="text-[#666]">This forum could not be found.</p>
          <Link to="/" className="mt-2 inline-block text-[#105B86] hover:underline">
            Return to the board index
          </Link>
        </div>
        <ForumFooter />
      </main>
    </div>
  );
}

function ForumPageSkeleton() {
  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-5">
        <Skeleton className="mb-4 h-8 w-full max-w-md rounded-[6px]" />
        <Skeleton className="mb-4 h-10 w-48 rounded-[6px]" />
        <div className="divide-y divide-[#ececec] rounded-[6px] border border-[#d6d6d6] bg-white">
          {[0, 1, 2, 3].map((r) => (
            <div key={r} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-10 w-10 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
